import './index.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.tsx';
import environment from './environment.ts';
import AnalyticsProvider from './contexts/analytics/analytics-provider.tsx';
import { APIProvider as GoogleApiProvider } from '@vis.gl/react-google-maps';
import { BrandingProvider } from './contexts/branding/branding-provider';
import { defaultEventPrefix } from './services/analytics-service.ts';
import sessionId from './services/session-service.ts';
import * as Sentry from '@sentry/react';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

type RootOptions = Parameters<typeof createRoot>[1];

let options: RootOptions;

if (environment.sentryEnabled) {
  Sentry.init({
    dsn: environment.sentryDsn,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    release: environment.releaseSha,
    environment: environment.environment,
  });

  options = {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  };
}

if (environment.dataDogEnabled) {
  const requestedExperience = new URLSearchParams(window.location.search).get('experience');

  datadogRum.init({
    applicationId: environment.dataDogApplicationId,
    clientToken: environment.dataDogClientToken,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'datadoghq.com',
    service: 'agencyapi-client',
    env: environment.environment,
    version: environment.releaseSha,
    enableExperimentalFeatures: ['feature_flags'],
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    compressIntakeRequests: true,
    allowedTracingUrls: [
      /https?:\/\/localhost(:\d+)?/,
      /https?:\/\/(.+\.)?joinroot.com(:\d+)?/,
      /https?:\/\/(.+\.)?root.com(:\d+)?/,
    ],
    trackSessionAcrossSubdomains: true,
  });

  datadogLogs.init({
    clientToken: environment.dataDogClientToken,
    site: 'datadoghq.com',
    service: 'agencyapi-client',
    env: environment.environment,
    version: environment.releaseSha,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: 'all',
    forwardReports: 'all',
    sessionSampleRate: 100,
  });

  // Datadog RUM setup breadcrumb:
  // if this log appears in Datadog Logs, the browser loaded this bundle,
  // ran the Datadog init block above, and attempted to start RUM/log collection.
  console.info('[hx-debug] rum_init', {
    message:
      'Datadog RUM initialization ran in the browser. HostedX loaded and attempted to start Datadog RUM and Logs.',
    clientSessionId: sessionId(),
    requestedExperience,
    host: window.location.host,
    href: window.location.href,
    env: environment.environment,
    appId: environment.dataDogApplicationId,
    releaseSha: environment.releaseSha,
  });

  const rumContext = datadogRum.getInternalContext();

  // Datadog RUM session breadcrumb:
  // if this log appears, Datadog returned a real RUM session_id after init,
  // which is stronger evidence than rum_init alone that a RUM session started.
  if (rumContext?.session_id) {
    console.info('[hx-debug] rum_session_started', {
      message:
        'Datadog RUM returned a session_id after initialization. A browser RUM session was successfully created.',
      clientSessionId: sessionId(),
      requestedExperience,
      rumSessionId: rumContext.session_id,
      appId: rumContext.application_id,
      viewId: rumContext.view?.id,
    });
  }

  datadogRum.startSessionReplayRecording();
}

const root = createRoot(document.getElementById('root')!, options);

root.render(
  <StrictMode>
    <AnalyticsProvider eventPrefix={defaultEventPrefix}>
      <GoogleApiProvider apiKey={environment.googleMapsApiKey}>
        <BrandingProvider>
          <App />
        </BrandingProvider>
      </GoogleApiProvider>
    </AnalyticsProvider>
  </StrictMode>
);
